import { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import TeamLogo from '../components/TeamLogo';
import MatchLiveStats from '../components/MatchLiveStats';
import { roundLabel } from '../utils/wcLabels';
import { shortTeamName } from '../components/teamUtils';
import useCompetitionStatus from '../hooks/useCompetitionStatus';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map NRL nicknames to full team names for logo matching
const NRL_NICKNAME_MAP = {
  "Broncos": "Brisbane Broncos",
  "Raiders": "Canberra Raiders",
  "Bulldogs": "Canterbury Bulldogs",
  "Sharks": "Cronulla Sharks",
  "Dolphins": "Dolphins",
  "Titans": "Gold Coast Titans",
  "Sea Eagles": "Manly Sea Eagles",
  "Storm": "Melbourne Storm",
  "Knights": "Newcastle Knights",
  "Warriors": "New Zealand Warriors",
  "Cowboys": "North Queensland Cowboys",
  "Eels": "Parramatta Eels",
  "Panthers": "Penrith Panthers",
  "Rabbitohs": "South Sydney Rabbitohs",
  "Dragons": "St George Illawarra Dragons",
  "Roosters": "Sydney Roosters",
  "Wests Tigers": "Wests Tigers",
};

function getStatusColor(status) {
  const s = status?.toLowerCase() || '';
  if (s === 'live' || s === 'in_progress' || s === 'inprogress' || s === '1h' || s === '2h' ||
      s === 'firsthalf' || s === 'first_half' || s === 'secondhalf' || s === 'second_half') return '#34C759';
  if (s === 'concluded' || s === 'postgame' || s === 'post_game' || s === 'post game' ||
      s === 'fulltime' || s === 'full time' || s === 'full_time' || s === 'ft' ||
      s === 'aet' || s === 'pen') return '#64748B';
  return '#FFCC00'; // upcoming
}

function getStatusLabel(status) {
  const s = status?.toLowerCase() || '';
  if (s === 'live' || s === 'in_progress' || s === 'inprogress' || s === 'firsthalf' ||
      s === 'first_half' || s === 'secondhalf' || s === 'second_half' || s === '1h' || s === '2h') return 'LIVE';
  if (s === 'concluded' || s === 'postgame' || s === 'post_game' || s === 'post game' ||
      s === 'fulltime' || s === 'full time' || s === 'full_time' || s === 'ft') return 'FT';
  if (s === 'aet') return 'AET';
  if (s === 'pen') return 'PEN';
  if (s === 'halftime' || s === 'half time' || s === 'half_time' || s === 'ht') return 'HT';
  if (s === 'pre_game' || s === 'pregame' || s === 'pre game') return 'About to start';
  if (s === 'upcoming' || s === 'scheduled' || s === 'unconfirmed_teams' || s === 'confirmed_teams' || s === 'ns') return 'Upcoming';
  if (s === 'postponed' || s === 'pst') return 'Postponed';
  if (s === 'cancelled' || s === 'abandoned' || s === 'canc') return 'Cancelled';
  return status;
}

function isLive(status) {
  const s = status?.toLowerCase() || '';
  return s === 'live' || s === 'in_progress' || s === 'inprogress' ||
    s === 'firsthalf' || s === 'first_half' || s === 'secondhalf' || s === 'second_half' ||
    s === 'halftime' || s === 'half_time' || s === '1h' || s === '2h' || s === 'ht';
}

function formatKickoff(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  // Display in AEST (UTC+10) / AEDT (UTC+11) 
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Australia/Sydney' }) + 
    ', ' + d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Sydney' });
}

function formatKickoffShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  // Display in AEST
  return d.toLocaleDateString('en-AU', { weekday: 'short', timeZone: 'Australia/Sydney' }) + ' ' +
    d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Sydney' });
}

function isFinished(status) {
  const s = status?.toLowerCase() || '';
  return s === 'concluded' || s === 'postgame' || s === 'post_game' || s === 'post game' ||
    s === 'fulltime' || s === 'full time' || s === 'full_time' || s === 'ft' ||
    s === 'aet' || s === 'pen';
}

function isUpcoming(status) {
  const s = status?.toLowerCase() || '';
  return s === 'upcoming' || s === 'scheduled' || s === 'unconfirmed_teams' ||
    s === 'confirmed_teams' || s === 'pre_game' || s === 'pregame' || s === 'pre game' ||
    s === 'ns' || s === '';
}

function formatQuarterClock(periodNumber, periodSeconds, sport) {
  if (periodNumber == null) return null;
  // AFL: Q1-Q4 plus extra time. NRL: H1/H2
  const half = sport === 'NRL';
  const label = half
    ? (periodNumber === 1 ? 'H1' : periodNumber === 2 ? 'H2' : `H${periodNumber}`)
    : `Q${periodNumber}`;
  if (periodSeconds == null) return label;
  // periodSeconds counts UP from start of quarter
  const m = Math.floor(periodSeconds / 60);
  const s = Math.abs(periodSeconds) % 60;
  return `${label} · ${m}:${String(s).padStart(2, '0')}`;
}

// Parse NRL `game_time` ("MM:SS") into "H1 · MM:SS" / "H2 · MM:SS"
function formatNrlClock(gameTime) {
  if (!gameTime || typeof gameTime !== 'string') return null;
  const match = gameTime.match(/^(\d{1,3}):(\d{1,2})$/);
  if (!match) return gameTime;
  const totalMins = parseInt(match[1], 10);
  const secs = parseInt(match[2], 10);
  const inSecondHalf = totalMins >= 40;
  const half = inSecondHalf ? 'H2' : 'H1';
  const minInHalf = inSecondHalf ? totalMins - 40 : totalMins;
  return `${half} · ${minInHalf}:${String(secs).padStart(2, '0')}`;
}

// WC live minute calculation from kickoff time (thesportsdb's strProgress is unreliable on free tier)
function computeWcMinute(startTime) {
  if (!startTime) return null;
  const koMs = new Date(startTime).getTime();
  if (isNaN(koMs)) return null;
  const elapsedMin = Math.floor((Date.now() - koMs) / 60000);
  if (elapsedMin < 0) return null;
  if (elapsedMin <= 45) return { half: 1, minute: elapsedMin };
  if (elapsedMin <= 60) return { half: 'HT', minute: null }; // 15-min break
  const secondHalfMin = elapsedMin - 15; // remove HT break
  if (secondHalfMin <= 90) return { half: 2, minute: secondHalfMin };
  return { half: 2, minute: 90, extraTime: secondHalfMin - 90 };
}

// WC clock from thesportsdb (status_raw: "1H", "2H", "HT", "FT" + optional progress)
function formatWcClock(game) {
  const raw = (game.status_raw || game.status || '').toUpperCase();
  if (raw === 'HT' || raw.includes('HALF TIME') || raw.includes('HALF_TIME')) return 'HT';
  if (raw.includes('ET') || raw.includes('EXTRA')) return 'ET';

  const tick = computeWcMinute(game.start_time);
  if (tick) {
    if (tick.half === 'HT') return 'HT';
    if (tick.half === 1) return `1H · ${tick.minute}'`;
    if (tick.half === 2) {
      if (tick.extraTime != null) return `2H · 90+${tick.extraTime}'`;
      return `2H · ${tick.minute}'`;
    }
  }
  // Fallback if no kickoff time
  if (raw.includes('1H') || raw.includes('FIRST HALF')) return '1H';
  if (raw.includes('2H') || raw.includes('SECOND HALF')) return '2H';
  return 'LIVE';
}

function leaderText(homeScore, awayScore, homeTeam, awayTeam, sport) {
  const h = homeScore || 0;
  const a = awayScore || 0;
  if (h === a) return 'Scores level';
  const leader = h > a ? homeTeam : awayTeam;
  const diff = Math.abs(h - a);
  if (sport === 'WC') {
    return `${leader} by ${diff} ${diff === 1 ? 'goal' : 'goals'}`;
  }
  const word = diff === 1 ? 'point' : 'points';
  return `${leader} by ${diff} ${word}`;
}

function resolveNrlTeamName(nickname) {
  return NRL_NICKNAME_MAP[nickname] || nickname;
}

// Match a live game to a fixture ID in tipStatus by team names
function findTipStatusForGame(tipStatus, homeTeam, awayTeam, sport) {
  for (const [fid, data] of Object.entries(tipStatus)) {
    if (!fid.toLowerCase().startsWith(sport.toLowerCase())) continue;
    // Match by any predicted winner being one of our two teams
    const teams = new Set([homeTeam, awayTeam]);
    if (data.entries?.some(e => teams.has(e.predicted_winner)) ||
        teams.has(data.current_leader)) {
      return data;
    }
  }
  return null;
}

export default function LiveScores() {
  const compStatus = useCompetitionStatus();
  const closedSet = new Set(compStatus?.closed_sports || []);
  const SPORT_TABS = [
    { key: 'AFL', color: '#E00000' },
    { key: 'NRL', color: '#007A33' },
    { key: 'EPL', color: '#37003C' },
    { key: 'AL',  color: '#F0423E' },
  ].filter(t => !closedSet.has(t.key));
  const [activeTab, setActiveTab] = useState(null);

  // Once compStatus loads (or on first render if no sports are closed), pick
  // the first active tab. Never leaves the user on a hidden sport.
  useEffect(() => {
    if (SPORT_TABS.length === 0) return;
    if (!activeTab || closedSet.has(activeTab)) {
      setActiveTab(SPORT_TABS[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compStatus]);
  const [aflGames, setAflGames] = useState([]);
  const [nrlGames, setNrlGames] = useState([]);
  const [wcGames, setWcGames] = useState([]);
  const [eplGames, setEplGames] = useState([]);
  const [alGames, setAlGames] = useState([]);
  const [aflRound, setAflRound] = useState(null);
  const [nrlRound, setNrlRound] = useState(null);
  const [wcRound, setWcRound] = useState(null);
  const [eplRound, setEplRound] = useState(null);
  const [alRound, setAlRound] = useState(null);
  const [tipStatus, setTipStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [wcLogos, setWcLogos] = useState({});
  const [eplLogos, setEplLogos] = useState({});
  const [alLogos, setAlLogos] = useState({});

  // Fetch WC + EPL + AL logo URLs once
  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/teams/WC/logos`)
      .then(res => mounted && setWcLogos(res.data || {}))
      .catch(() => mounted && setWcLogos({}));
    axios.get(`${API_URL}/api/teams/EPL/logos`)
      .then(res => mounted && setEplLogos(res.data || {}))
      .catch(() => mounted && setEplLogos({}));
    axios.get(`${API_URL}/api/teams/AL/logos`)
      .then(res => mounted && setAlLogos(res.data || {}))
      .catch(() => mounted && setAlLogos({}));
    return () => { mounted = false; };
  }, []);

  const fetchScores = useCallback(async () => {
    try {
      // Determine current round on first load
      let aflRoundNum = aflRound;
      let nrlRoundNum = nrlRound;
      let wcRoundNum = wcRound;
      let eplRoundNum = eplRound;
      let alRoundNum = alRound;
      if (aflRoundNum == null) {
        try {
          const r = await axios.get(`${API_URL}/api/current-round/AFL`, { withCredentials: true });
          aflRoundNum = r.data.round_number;
        } catch { aflRoundNum = 12; }
      }
      if (nrlRoundNum == null) {
        try {
          const r = await axios.get(`${API_URL}/api/current-round/NRL`, { withCredentials: true });
          nrlRoundNum = r.data.round_number;
        } catch { nrlRoundNum = 12; }
      }
      if (wcRoundNum == null) {
        try {
          const r = await axios.get(`${API_URL}/api/current-round/WC`, { withCredentials: true });
          wcRoundNum = r.data.round_number;
        } catch { wcRoundNum = 1; }
      }
      if (eplRoundNum == null) {
        try {
          const r = await axios.get(`${API_URL}/api/current-round/EPL`, { withCredentials: true });
          eplRoundNum = r.data.round_number;
        } catch { eplRoundNum = 1; }
      }
      if (alRoundNum == null) {
        try {
          const r = await axios.get(`${API_URL}/api/current-round/AL`, { withCredentials: true });
          alRoundNum = r.data.round_number;
        } catch { alRoundNum = 1; }
      }

      const [aflRes, nrlRes, wcRes, eplRes, alRes] = await Promise.all([
        axios.get(`${API_URL}/api/live-scores/afl?round_number=${aflRoundNum}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/live-scores/nrl?round_number=${nrlRoundNum}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/live-scores/wc?round_number=${wcRoundNum}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/live-scores/epl?round_number=${eplRoundNum}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/live-scores/al?round_number=${alRoundNum}`, { withCredentials: true }).catch(() => ({ data: { games: [] } })),
      ]);

      setAflGames(aflRes.data.games || []);
      setNrlGames(nrlRes.data.games || []);
      setWcGames(wcRes.data.games || []);
      setEplGames(eplRes.data.games || []);
      setAlGames(alRes.data.games || []);

      if (aflRound == null) setAflRound(aflRoundNum);
      if (nrlRound == null) setNrlRound(nrlRoundNum);
      if (wcRound == null) setWcRound(wcRoundNum);
      if (eplRound == null) setEplRound(eplRoundNum);
      if (alRound == null) setAlRound(alRoundNum);

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching live scores:', err);
    } finally {
      setLoading(false);
    }
  }, [aflRound, nrlRound, wcRound, eplRound, alRound]);

  // Fetch tip status for the current round
  const fetchTipStatus = useCallback(async () => {
    try {
      const sport = activeTab;
      const roundNum =
        activeTab === 'AFL' ? (aflRound || 12) :
        activeTab === 'NRL' ? (nrlRound || 12) :
        activeTab === 'EPL' ? (eplRound || 1) :
        activeTab === 'AL'  ? (alRound || 1) :
        (wcRound || 1);
      const res = await axios.get(
        `${API_URL}/api/live-tips-status?sport=${sport}&round_number=${roundNum}`,
        { withCredentials: true }
      );
      setTipStatus(res.data || {});
    } catch {
      setTipStatus({});
    }
  }, [activeTab, aflRound, nrlRound, wcRound, eplRound, alRound]);

  useEffect(() => {
    fetchScores();
    const interval = setInterval(fetchScores, 30000);
    return () => clearInterval(interval);
  }, [fetchScores]);

  useEffect(() => {
    fetchTipStatus();
    const interval = setInterval(fetchTipStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchTipStatus]);

  const changeRound = (sport, direction) => {
    if (sport === 'AFL') {
      // AFL: 23 home-and-away rounds + finals (Wildcard=24, QF=25, SF=26, PF=27, GF=28).
      setAflRound(prev => Math.max(0, Math.min(28, (prev || 12) + direction)));
    } else if (sport === 'NRL') {
      // NRL: 27 home-and-away rounds + finals (28..31 → Wk1, Wk2, Prelims, GF).
      setNrlRound(prev => Math.max(1, Math.min(31, (prev || 12) + direction)));
    } else if (sport === 'EPL') {
      // EPL: 38 rounds in a Premier League season
      setEplRound(prev => Math.max(1, Math.min(38, (prev || 1) + direction)));
    } else if (sport === 'AL') {
      // A-League: 26 regular rounds + 5 finals rounds
      setAlRound(prev => Math.max(1, Math.min(31, (prev || 1) + direction)));
    } else {
      // WC: 9 rounds total
      setWcRound(prev => Math.max(1, Math.min(9, (prev || 1) + direction)));
    }
  };

  const currentGames =
    activeTab === 'AFL' ? aflGames :
    activeTab === 'NRL' ? nrlGames :
    activeTab === 'EPL' ? eplGames :
    activeTab === 'AL'  ? alGames :
    wcGames;
  const currentRound =
    activeTab === 'AFL' ? aflRound :
    activeTab === 'NRL' ? nrlRound :
    activeTab === 'EPL' ? eplRound :
    activeTab === 'AL'  ? alRound :
    wcRound;

  // Final Scores section: show only completed games for the selected round
  const sortedGames = [...currentGames]
    .filter(g => isFinished(g.status))
    .sort((a, b) => {
      const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
      const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
      return ta - tb;
    });

  return (
    <div className="tipster-card" data-testid="live-scores-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-[#007AFF]" />
          <h3 className="text-lg font-bold uppercase tracking-tight text-[#1A1A1A]">Final Scores</h3>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-gray-400 hidden sm:block">
              Updated {lastUpdated.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-[#007AFF] hover:bg-blue-50"
            onClick={fetchScores}
            data-testid="refresh-scores-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Sport Tabs — closed sports are hidden automatically */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-sm p-1">
        {SPORT_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 py-2 px-4 text-sm font-semibold uppercase tracking-wider rounded-sm transition-all ${
              activeTab === t.key ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === t.key ? { color: t.color } : {}}
            data-testid={`live-tab-${t.key.toLowerCase()}`}
          >
            {t.key}
          </button>
        ))}
      </div>

      {/* Round Selector */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={() => changeRound(activeTab, -1)}
          className="p-1 text-gray-400 hover:text-[#1A1A1A] transition-colors"
          data-testid="prev-round-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-sm font-semibold uppercase tracking-wider text-gray-600 min-w-[180px] text-center">
          {roundLabel(activeTab, currentRound || 1)}
        </span>
        <button
          onClick={() => changeRound(activeTab, 1)}
          className="p-1 text-gray-400 hover:text-[#1A1A1A] transition-colors"
          data-testid="next-round-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Games List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sortedGames.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No matches this round</p>
          </div>
        ) : (
          sortedGames.map((game, idx) => {
            const homeTeamName = activeTab === 'NRL' ? resolveNrlTeamName(game.home_team) : game.home_team;
            const awayTeamName = activeTab === 'NRL' ? resolveNrlTeamName(game.away_team) : game.away_team;
            const isWC = activeTab === 'WC';
            const live = isLive(game.status);
            const finished = isFinished(game.status);
            const upcoming = isUpcoming(game.status);
            const statusColor = getStatusColor(game.status);
            const statusLabel = getStatusLabel(game.status);
            const kickoff = formatKickoffShort(game.start_time);
            const matchTime = game.game_time || game.timestr || '';

            // Find tip status for this game
            const gameStatus = (live || finished) ? findTipStatusForGame(tipStatus, homeTeamName, awayTeamName, activeTab) : null;

            return (
              <div
                key={idx}
                className={`rounded-sm transition-all overflow-hidden ${
                  live ? 'bg-green-50 border border-green-200' : 'bg-gray-50 hover:bg-gray-100'
                }`}
                data-testid={`live-game-${idx}`}
              >
                {/* Kickoff time row */}
                <div className="flex items-center justify-between px-3 pt-2 pb-1">
                  <span className="text-xs text-gray-400">
                    {kickoff}
                  </span>
                  <span className="text-xs text-gray-400">
                    {game.venue}
                  </span>
                </div>

                {/* Teams & scores row */}
                <div className="flex items-center gap-3 px-3 pb-2">
                  {/* Home */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <TeamLogo teamName={homeTeamName} logoUrl={isWC ? wcLogos?.[homeTeamName] : (activeTab === 'EPL' ? eplLogos?.[homeTeamName] : (activeTab === 'AL' ? alLogos?.[homeTeamName] : undefined))} size={32} />
                    <span className="font-semibold text-sm text-[#1A1A1A] truncate">
                      {activeTab === 'NRL' ? game.home_team : shortTeamName(game.home_team)}
                    </span>
                  </div>

                  {/* Score + Status */}
                  <div className="flex flex-col items-center flex-shrink-0 min-w-[110px]">
                    {upcoming ? (
                      <span className="text-xs font-semibold text-gray-400 uppercase">
                        {statusLabel}
                      </span>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-black tabular-nums ${live ? 'text-[#1A1A1A]' : 'text-gray-700'}`}>
                            {game.home_score || 0}
                          </span>
                          <span className="text-xs text-gray-400">-</span>
                          <span className={`text-lg font-black tabular-nums ${live ? 'text-[#1A1A1A]' : 'text-gray-700'}`}>
                            {game.away_score || 0}
                          </span>
                        </div>
                        <span
                          className="text-xs font-bold uppercase px-1.5 py-0.5 rounded-sm mt-0.5 whitespace-nowrap"
                          style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
                          data-testid={`live-clock-${idx}`}
                        >
                          {(() => {
                            if (live) {
                              if (activeTab === 'NRL') {
                                return formatNrlClock(game.game_time) || statusLabel;
                              }
                              if (activeTab === 'WC') {
                                return formatWcClock(game) || statusLabel;
                              }
                              const qc = formatQuarterClock(game.period_number, game.period_seconds, activeTab);
                              return qc || (matchTime || statusLabel);
                            }
                            return statusLabel;
                          })()}
                        </span>
                        {live && (game.home_score !== game.away_score) && (
                          <span className="text-[10px] text-gray-500 mt-0.5" data-testid={`live-leader-${idx}`}>
                            {leaderText(game.home_score, game.away_score, homeTeamName, awayTeamName, activeTab)}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Away */}
                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                    <span className="font-semibold text-sm text-[#1A1A1A] truncate text-right">
                      {activeTab === 'NRL' ? game.away_team : shortTeamName(game.away_team)}
                    </span>
                    <TeamLogo teamName={awayTeamName} logoUrl={isWC ? wcLogos?.[awayTeamName] : (activeTab === 'EPL' ? eplLogos?.[awayTeamName] : (activeTab === 'AL' ? alLogos?.[awayTeamName] : undefined))} size={32} />
                  </div>
                </div>

                {/* Tip Status Row */}
                {gameStatus && gameStatus.entries && gameStatus.entries.length > 0 && (
                  <div className="px-3 pb-2 pt-1 border-t border-gray-200/60">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {gameStatus.entries.map((entry) => (
                        <div key={entry.user_id} className="flex items-center gap-1.5">
                          <span className={`w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                            entry.is_winning ? 'bg-[#34C759]' : 'bg-[#FF3B30]'
                          }`}>
                            {entry.name.charAt(0)}
                          </span>
                          <span className={`text-xs font-semibold ${entry.is_winning ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                            {entry.name}
                          </span>
                          <span className={`text-xs font-bold ${entry.points > 0 ? 'text-[#007AFF]' : 'text-gray-400'}`}>
                            {entry.points > 0 ? `+${entry.points}` : '0'}
                          </span>
                          {entry.bonus && entry.bonus.length > 0 && (
                            <span className="text-xs text-[#FFB81C]" title={entry.bonus.join(', ')}>
                              {entry.bonus.map(b => b.includes('Exact') ? '!!!' : b.includes('Closest') ? '!' : '').join('')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Live Stats panel (only when match has started; AFL/NRL/EPL) */}
                {(live || finished) && !isWC && (
                  <div className="px-3 pb-2">
                    <MatchLiveStats
                      sport={activeTab}
                      homeTeam={homeTeamName}
                      awayTeam={awayTeamName}
                      roundNumber={game.round_number || currentRound}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
